import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { copyToClipboard, getCopyIcon } from '../../utils/copyUtils';

/**
 * VoiceAILoginModal Component
 *
 * Modal dialog for authenticating with Voice AI Provider (RetellAI).
 * Handles both username/password login and OTP verification flows.
 *
 * Props:
 * - show: boolean - Control modal visibility
 * - onHide: function - Callback when modal is closed
 * - onSuccess: function - Callback when authentication succeeds
 */
const VoiceAILoginModal = ({ show, onHide, onSuccess }) => {
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp' | 'success'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [sessionId, setSessionId] = useState(null); // Store session ID from login response
  const [mfaMethod, setMfaMethod] = useState('sms'); // 'sms' or 'email'
  const [switchingMethod, setSwitchingMethod] = useState(false);
  const [successData, setSuccessData] = useState({ jwt: null, orgId: null }); // Store success data
  const [copyIconState, setCopyIconState] = useState({});

  // Keep these for backward compatibility (org_id step is removed but code still references them)
  const [orgId, setOrgId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const fetchOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const response = await adminApi.voiceaiGetOrganizations();
      if (response.data.success) {
        setOrganizations(response.data.organizations || []);
      } else {
        setError('Failed to load organizations. You can enter org ID manually.');
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      setError('Failed to load organizations. You can enter org ID manually.');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await adminApi.voiceaiLogin({ username, password });

      if (response.data.success) {
        // Login successful - show success screen with extracted data
        setSuccessData({
          jwt: response.data.jwt_preview,
          orgId: response.data.org_id
        });
        setStep('success');
      } else if (response.data.requires_otp) {
        // Store session_id, MFA method, and move to OTP step
        setSessionId(response.data.session_id);
        setMfaMethod(response.data.mfa_method || 'sms');
        setStep('otp');
      } else {
        setError(response.data.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please check your credentials and try again.';
      setError(errorMessage);
      console.error('Voice AI Provider login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchMFAMethod = async (newMethod) => {
    if (newMethod === mfaMethod) return; // Already using this method

    setSwitchingMethod(true);
    setError(null);

    try {
      const response = await adminApi.voiceaiSelectMFAMethod({
        method: newMethod,
        session_id: sessionId
      });

      if (response.data.success) {
        setMfaMethod(newMethod);
        setOtpCode(''); // Clear OTP code when switching methods
      } else {
        setError(response.data.error || 'Failed to switch MFA method. Please try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to switch MFA method. Please try again.';
      setError(errorMessage);
      console.error('MFA method switch error:', err);
    } finally {
      setSwitchingMethod(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await adminApi.voiceaiVerifyOTP({
        otp_code: otpCode,
        session_id: sessionId,
        method: mfaMethod
      });

      if (response.data.success) {
        // OTP verified successfully - show success screen with extracted data
        setSuccessData({
          jwt: response.data.jwt_preview,
          orgId: response.data.org_id
        });
        setStep('success');
      } else {
        const errorMsg = response.data.error || 'OTP verification failed.';

        // Check if session expired - go back to login
        if (errorMsg.toLowerCase().includes('session expired') ||
            errorMsg.toLowerCase().includes('please login again')) {
          setError(errorMsg + ' Redirecting to login...');
          setTimeout(() => {
            setStep('credentials');
            setOtpCode('');
            setSessionId(null);
            setError(null);
          }, 2000);
        } else {
          setError(errorMsg);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'OTP verification failed. Please try again.';

      // Check if session expired - go back to login
      if (errorMessage.toLowerCase().includes('session expired') ||
          errorMessage.toLowerCase().includes('please login again')) {
        setError(errorMessage + ' Redirecting to login...');
        setTimeout(() => {
          setStep('credentials');
          setOtpCode('');
          setSessionId(null);
          setError(null);
        }, 2000);
      } else {
        setError(errorMessage);
      }

      console.error('Voice AI Provider OTP error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgIdSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await adminApi.voiceaiSaveCredentials({ org_id: orgId });

      if (response.data.success) {
        onSuccess();
        onHide();
        resetForm();
      } else {
        setError(response.data.error || 'Failed to save credentials.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to save credentials. Please try again.';
      setError(errorMessage);
      console.error('Save credentials error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipOrgId = () => {
    // Close without saving to database
    onSuccess();
    onHide();
    resetForm();
  };

  const resetForm = useCallback(() => {
    setStep('credentials');
    setUsername('');
    setPassword('');
    setOtpCode('');
    setOrgId('');
    setSessionId(null);
    setOrganizations([]);
    setMfaMethod('sms');
    setSwitchingMethod(false);
    setSuccessData({ jwt: null, orgId: null });
    setCopyIconState({});
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onHide();
  }, [onHide, resetForm]);

  const handleBackToLogin = () => {
    setStep('credentials');
    setOtpCode('');
    setSessionId(null);
    setMfaMethod('sms');
    setSwitchingMethod(false);
    setError(null);
  };

  const handleModalClick = (e) => {
    // Close modal if clicking on the backdrop (not on modal content)
    if (e.target.id === 'voiceAILoginModal') {
      handleClose();
    }
  };

  // Use Bootstrap's modal visibility classes and handle ESC key
  useEffect(() => {
    const modalElement = document.getElementById('voiceAILoginModal');

    // Handle ESC key
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && show) {
        handleClose();
      }
    };

    if (show && modalElement) {
      modalElement.classList.add('show');
      modalElement.style.display = 'block';
      document.body.classList.add('modal-open');

      // Add ESC key listener
      document.addEventListener('keydown', handleEscKey);

      // Add backdrop
      let backdrop = document.querySelector('.modal-backdrop');
      if (!backdrop) {
        backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        backdrop.style.cursor = 'pointer';
        backdrop.addEventListener('click', handleClose);
        document.body.appendChild(backdrop);
      }
    }

    // Cleanup function
    return () => {
      document.removeEventListener('keydown', handleEscKey);

      if (modalElement) {
        modalElement.classList.remove('show');
        modalElement.style.display = 'none';
      }
      document.body.classList.remove('modal-open');

      // Remove all backdrops
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach(backdrop => {
        backdrop.removeEventListener('click', handleClose);
        backdrop.remove();
      });
    };
  }, [show, handleClose]);

  if (!show) return null;

  return (
    <div
      className="modal fade"
      id="voiceAILoginModal"
      tabIndex="-1"
      aria-labelledby="voiceAILoginModalLabel"
      aria-hidden="true"
      onClick={handleModalClick}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <span className="text-lg" id="voiceAILoginModalLabel">
              {step === 'credentials' ? (
                <>
                  Voice AI Provider Login
                </>
              ) : step === 'otp' ? (
                <>
                  Enter OTP Code
                </>
              ) : step === 'success' ? (
                <>
                  <i className="fas fa-check-circle text-success me-2"></i>
                  Authentication Successful
                </>
              ) : (
                <>
                  Enter Organization ID
                </>
              )}
            </span>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body">
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            )}

            {step === 'credentials' && (
              <form onSubmit={handleLogin}>
                <div className="mb-3">
                  <label htmlFor="voiceai-username" className="form-label">
                    {/* <i className="fas fa-envelope me-2"></i> */}
                    Username/Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="voiceai-username"
                    placeholder=""
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="voiceai-password" className="form-label">
                    {/* <i className="fas fa-lock me-2"></i> */}
                    Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="voiceai-password"
                    placeholder=""
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading || !username || !password}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Logging in...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sign-in-alt me-2"></i>
                      Login
                    </>
                  )}
                </button>
              </form>
            )}

            {step === 'otp' && (
              <form onSubmit={handleOTPSubmit}>
                <div className="alert alert-info" role="alert">
                  <i className={mfaMethod === 'email' ? 'fas fa-envelope me-2' : 'fas fa-mobile-alt me-2'}></i>
                  {mfaMethod === 'email'
                    ? 'Please check your email for the OTP code.'
                    : 'Please check your authenticator app or SMS for the OTP code.'}
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    <i className="fas fa-shield-alt me-2"></i>
                    Verification Method
                  </label>
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn ${mfaMethod === 'sms' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handleSwitchMFAMethod('sms')}
                      disabled={loading || switchingMethod}
                    >
                      {switchingMethod && mfaMethod !== 'sms' ? (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      ) : (
                        <i className="fas fa-mobile-alt me-1"></i>
                      )}
                      SMS / App
                    </button>
                    <button
                      type="button"
                      className={`btn ${mfaMethod === 'email' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handleSwitchMFAMethod('email')}
                      disabled={loading || switchingMethod}
                    >
                      {switchingMethod && mfaMethod !== 'email' ? (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      ) : (
                        <i className="fas fa-envelope me-1"></i>
                      )}
                      Email
                    </button>
                  </div>
                  <div className="form-text">
                    {mfaMethod === 'email'
                      ? 'Check your email inbox for the verification code'
                      : 'Use your authenticator app or check SMS for the code'}
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="voiceai-otp" className="form-label">
                    <i className="fas fa-key me-2"></i>
                    OTP Code
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="voiceai-otp"
                    placeholder="Enter 6-digit code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    maxLength={6}
                    required
                    disabled={loading || switchingMethod}
                    autoFocus
                    style={{ fontSize: '1.25rem', letterSpacing: '0.5rem', textAlign: 'center' }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100 mb-2"
                  disabled={loading || switchingMethod || otpCode.length < 4}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify OTP
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-link w-100"
                  onClick={handleBackToLogin}
                  disabled={loading || switchingMethod}
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Login
                </button>
              </form>
            )}

            {step === 'success' && (
              <div>
                <div className="alert alert-success" role="alert">
                  <i className="fas fa-check-circle me-2"></i>
                  Credential saved automatically! You can now access Voice AI analytics.
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">
                    <i className="fas fa-key me-2"></i>
                    JWT Token (Preview)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control font-monospace small"
                      value={successData.jwt || 'N/A'}
                      readOnly
                      style={{ fontSize: '0.85rem' }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => copyToClipboard(successData.jwt, setCopyIconState, 'jwt')}
                      title="Copy to clipboard"
                    >
                      <i className={`fas ${getCopyIcon(copyIconState.jwt)}`}></i>
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-bold">
                    <i className="fas fa-building me-2"></i>
                    Organization ID
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control font-monospace small"
                      value={successData.orgId || 'Not found'}
                      readOnly
                      style={{ fontSize: '0.85rem' }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => copyToClipboard(successData.orgId, setCopyIconState, 'orgId')}
                      title="Copy to clipboard"
                      disabled={!successData.orgId}
                    >
                      <i className={`fas ${getCopyIcon(copyIconState.orgId)}`}></i>
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={() => {
                    onSuccess();
                    onHide();
                    resetForm();
                  }}
                >
                  <i className="fas fa-check me-2"></i>
                  Done
                </button>
              </div>
            )}

            {step === 'org_id' && (
              <form onSubmit={handleOrgIdSubmit}>
                <div className="alert alert-info" role="alert">
                  <i className="fas fa-info-circle me-2"></i>
                  Select your RetellAI Organization to save credentials for future use.
                  You can skip this step if you only want temporary access.
                </div>

                {loadingOrgs ? (
                  <div className="text-center mb-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Loading organizations...
                  </div>
                ) : organizations.length > 0 ? (
                  <div className="mb-4">
                    <label htmlFor="voiceai-org-id" className="form-label">
                      <i className="fas fa-building me-2"></i>
                      Organization
                    </label>
                    <select
                      className="form-select"
                      id="voiceai-org-id"
                      value={orgId}
                      onChange={(e) => setOrgId(e.target.value)}
                      disabled={loading}
                      autoFocus
                    >
                      <option value="">-- Select Organization --</option>
                      {organizations.map((org) => (
                        <option key={org.organization_id} value={org.organization_id}>
                          {org.organization_name || org.organization_id}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Select the organization workspace you want to use
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    <label htmlFor="voiceai-org-id" className="form-label">
                      <i className="fas fa-building me-2"></i>
                      Organization ID
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="voiceai-org-id"
                      placeholder="Enter Organization ID (e.g., org_abc123)"
                      value={orgId}
                      onChange={(e) => setOrgId(e.target.value)}
                      disabled={loading}
                      autoFocus
                    />
                    <div className="form-text">
                      Enter your organization ID manually
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-100 mb-2"
                  disabled={loading || !orgId}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Credentials
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-link w-100"
                  onClick={handleSkipOrgId}
                  disabled={loading}
                >
                  <i className="fas fa-forward me-2"></i>
                  Skip (Use Session Only)
                </button>
              </form>
            )}
          </div>

          <div className="modal-footer bg-light">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              This login is required to access Voice AI Provider analytics and management features.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceAILoginModal;
